import type { Map } from 'ol';

/**
 * Far longer than a failing tile takes (fetchTileWithRetry gives up after 5
 * retries, about 4 s of backoff); only a tile request that stalls lasts this
 * long.
 */
export const RENDER_COMPLETE_TIMEOUT_MS = 30_000;

/**
 * Resolves on the map's next `rendercomplete`. Rejects if `signal` aborts or
 * rendering hasn't finished within `timeoutMs`, so a caller that disposes the
 * map in `finally` always gets there, even when a tile request hangs.
 */
export function waitForRenderComplete(
  map: Pick<Map, 'once'>,
  signal?: AbortSignal,
  timeoutMs = RENDER_COMPLETE_TIMEOUT_MS,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      reject(new Error(`Map did not finish rendering within ${timeoutMs} ms`));
    }, timeoutMs);

    signal?.addEventListener('abort', onAbort, { once: true });
    map.once('rendercomplete', () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      resolve();
    });
  });
}
